import { inject, Injectable, Injector, OnDestroy } from '@angular/core';
import {
  ApizUIEditTab,
  ApizUIFieldEntity,
  ApizUIForm,
  ApizUIWindowColumnInfoDef,
  ProcessRequest,
} from '@compiere-ws/models/apiz-domains';
import { CompiereMenu } from '@compiere-ws/models/compiere-menu-json';
import { SpecificWindowCompiereWS } from '@compiere-ws/models/specific-window-json';
import { CompiereTab, WindowCompiereWS } from '@compiere-ws/models/window-json';
import { CompiereCalloutService } from '@compiere-ws/services/compiere-callout/compiere-callout.service';
import { CompiereDataService } from '@compiere-ws/services/compiere-data/compiere-data.service';
import { LocationService } from '@compiere-ws/services/compiere-location/location.service';
import { CompiereMenuService } from '@compiere-ws/services/compiere-menu/compiere-menu.service';
import { CompiereMenuFavoritesService } from '@compiere-ws/services/compiere-menufavorites/compiere-menufavorites.service';
import { CompiereProcessService } from '@compiere-ws/services/compiere-process/compiere-process.service';
import { DashboardService } from '@compiere-ws/services/dashboard/dashboard.service';
import { PrinterService } from '@compiere-ws/services/printer-service/printer-service.service';
import { TagService } from '@compiere-ws/services/tag/tag.service';
import { WidgetCenterService } from '@compiere-ws/services/widget-center/widget-center.service';
import { WindowsService } from '@compiere-ws/services/windows/windows.service';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { ApizTabUI, IupicsField, IupicsSpecificWindow } from '@iupics-manager/models/iupics-data';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { IupicsColumnInfo } from '@iupics-manager/models/iupics_column_info';
import { SubscriptionsUtils } from '@iupics-util/tools/subscriptions.util';
import { TranslateService } from '@ngx-translate/core';
import { IupicsMenuType, MenuCategoryUI, MenuFavoritesCategoryUI, MenuItemUI } from '@web-desktop/models/menu-item-ui';
import { cloneDeep } from 'lodash';
import { Observable, of, Subscription, throwError, zip } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { MessageManagerService } from '../message/message-manager.service';
import { SecurityManagerService } from '../security-manager/security-manager.service';
import { MenuCreatorUtils } from './utils/ui-creator-menu.utils';
import { SpecificWindowsCreatorUtils } from './utils/ui-creator-specific-windows.utils';
import { UICreatorUtils } from './utils/ui-creator.utils';

@Injectable({
  providedIn: 'root',
})
export class UICreatorService implements OnDestroy {
  readonly #injector = inject(Injector);
  readonly #menuService = inject(CompiereMenuService);
  readonly #menuFavoritesService = inject(CompiereMenuFavoritesService);
  readonly #widgetCenterService = inject(WidgetCenterService);
  readonly #windowService = inject(WindowsService);
  readonly #calloutService = inject(CompiereCalloutService);
  readonly #processSevice = inject(CompiereProcessService);
  readonly #compiereDataService = inject(CompiereDataService);
  readonly #dataStore = inject(DataStoreService);
  readonly #messageManager = inject(MessageManagerService);
  readonly #printerService = inject(PrinterService);
  readonly #connectorService = inject(SecurityManagerService);
  readonly #translateService = inject(TranslateService);
  readonly #cacheService = inject(CacheManagerService);
  readonly #locationService = inject(LocationService);
  readonly #dashboardService = inject(DashboardService);
  readonly #tagService = inject(TagService);
  private subscriptions: Subscription[] = [];
  fieldGroup: IupicsField;

  private iupicsMenus$: Observable<MenuItemUI[]>;
  private iupicsMenuFavorites: MenuItemUI[];
  private iupicsMenuCategories: MenuCategoryUI[];
  private iupicsMenuFavoritesCategories: MenuFavoritesCategoryUI[];
  private iupicsWindowDefaultTab = new Map<number, number>();
  private iupicsTabs = new Map<string, ApizTabUI>();
  private iupicsWindowNames = new Map<number, string>();
  private iupicsSpecificWindows = new Map<number, IupicsSpecificWindow>();
  private iupicsSpecificWindowsErrors = new Map<number, any>();
  private iupicsSpecificWindowsValueID = new Map<string, number>();
  private iupicsLocationPanels = new Map<number, Observable<IupicsSpecificWindow>>();
  private compiereTabs = new Map<number, Observable<CompiereTab>>();

  constructor() {
    UICreatorUtils.setDataStore(this.#dataStore);
    UICreatorUtils.setConnectorService(this.#connectorService);
    UICreatorUtils.setTranslateService(this.#translateService);
    SpecificWindowsCreatorUtils.setDataStore(this.#dataStore);
    SpecificWindowsCreatorUtils.setConnectorService(this.#connectorService);
  }

  resetCachingData(): void {
    this.iupicsWindowDefaultTab = new Map();
    this.iupicsTabs = new Map();
    this.iupicsMenus$ = undefined;
    this.iupicsMenuFavorites = undefined;
    this.iupicsMenuCategories = [];
    this.iupicsMenuFavoritesCategories = [];
    this.iupicsLocationPanels = new Map();
    this.compiereTabs = new Map();
    this.#dashboardService.resetCachingData();
    this.#widgetCenterService.resetCachingData();
    this.iupicsSpecificWindows = new Map();
    this.#cacheService.resetCachingData();
    this.#tagService.resetCachingData();
  }

  getIupicsMenusCategories(): MenuCategoryUI[] {
    return this.iupicsMenuCategories;
  }

  getIupicsMenuFavoritesCategories(): MenuFavoritesCategoryUI[] {
    return this.iupicsMenuFavoritesCategories;
  }

  getIupicsMenus(): Observable<MenuItemUI[]> {
    if (!this.iupicsMenus$) {
      this.iupicsMenus$ = this.#menuService.getMenus().pipe(
        map((compiereMenus) => {
          this.iupicsMenuCategories = [];
          const [iupicsMenus, iupicsMenuCategories] = MenuCreatorUtils.transformMenus([], compiereMenus);
          this.iupicsMenuCategories = iupicsMenuCategories;

          this.iupicsMenuCategories.push(
            {
              id: -1,
              name: this.#translateService.instant('menu.category_all'),
              icon: 'fas fa-folder',
            },
            {
              id: 0,
              name: this.#translateService.instant('menu.category_mostAccurate'),
              icon: '',
            },
            {
              id: Number.POSITIVE_INFINITY,
              name: this.#translateService.instant('menu.category_undefined'),
              icon: 'fas fa-not-equal',
            }
          );

          this.iupicsMenuCategories = MenuCreatorUtils.sortCategories(this.iupicsMenuCategories);

          return iupicsMenus;
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        }),
        map(cloneDeep) // used to have a copy of the object each time we sub to avoid editing the object stored by the obs
      );
    }
    return this.iupicsMenus$;
  }

  getIupicsMenuFromWindow(window_id: number, menuType: IupicsMenuType): Observable<MenuItemUI> {
    return this.getIupicsMenus().pipe(
      map((menus) => menus.find((menu) => menu.actionID === window_id && menu.menuType === menuType))
    );
  }

  getIupicsMenuFavorites(forceRefresh = false): Observable<MenuItemUI[]> {
    if (!forceRefresh && this.iupicsMenuFavorites) {
      return of(this.iupicsMenuFavorites);
    } else {
      return this.#menuFavoritesService.getMenuFavorites().pipe(
        map((compiereMenuFavorites) => {
          this.iupicsMenuFavoritesCategories = [];
          const [iupicsMenuFavorites, _] = MenuCreatorUtils.transformMenus(
            this.iupicsMenuFavoritesCategories,
            compiereMenuFavorites,
            true
          );
          this.iupicsMenuFavorites = iupicsMenuFavorites;
          return this.iupicsMenuFavorites;
        })
      );
    }
  }

  setIupicsFavorites(menuItem: CompiereMenu[]): Observable<CompiereMenu[]> {
    return this.#menuFavoritesService.setMenuFavorites(menuItem);
  }

  deleteIupicsFavorites(parentId: number, menuId: number, isSummary: boolean): Observable<boolean> {
    return this.#menuFavoritesService.deleteMenuFavorites(parentId, menuId, isSummary);
  }

  getProductAttribute(tab_id: number, ctx: any): Observable<any> {
    return this.#windowService.getProductAttribute(tab_id, ctx);
  }

  saveProductAttribute(
    attributeInstance_id: number,
    product_ID: number,
    tab_id: number,
    fieldId: number,
    params: any,
    ctx: any
  ): Observable<any> {
    return this.#windowService.saveProductAttribute(attributeInstance_id, product_ID, tab_id, fieldId, params, ctx);
  }
  saveProductAttributeLot(product_ID: number): Observable<any> {
    return this.#windowService.saveProductAttributeLot(product_ID);
  }
  saveProductAttributeSerie(product_ID: number): Observable<any> {
    return this.#windowService.saveProductAttributeSerie(product_ID);
  }

  generateKey(windowType: 'WINDOW' | 'FORM', windowId: number, tabId?: number): string {
    let tabPart = '';
    if (windowType === 'WINDOW') {
      tabPart = tabId !== undefined ? '_' + tabId : '';
    }
    return windowType + '_' + windowId + tabPart;
  }

  getWindow(windowId: number): Observable<ApizTabUI> {
    if (this.iupicsWindowDefaultTab.has(windowId)) {
      const key = this.generateKey('WINDOW', windowId, this.iupicsWindowDefaultTab.get(windowId));
      return of(cloneDeep(this.iupicsTabs.get(key)));
    } else {
      return this.#windowService.getWindowUI(windowId).pipe(
        map((apizWindow) => {
          // get tabs sorted by seqno
          const tabs = Object.values(apizWindow.tabs).sort((t1, t2) => (t1.seqNo ?? 99999) - (t2.seqNo ?? 99999));
          // register the datastructures
          for (const key of Object.keys(apizWindow.datastructures)) {
            this.#dataStore.addWindowDataStructure(parseInt(key, 10), apizWindow.datastructures[key]);
          }
          // setup the children of each tabs
          for (const tab of tabs) {
            const editTabs: ApizUIEditTab[] = [];
            const additionalTabs: ApizUIEditTab[] = [];
            const { key, tabId } = tab;
            // if tablevel == 0, we register it as default tab of the window
            if (tab.data.tabLevel === 0) {
              this.iupicsWindowDefaultTab.set(windowId, tab.tabId);
            }
            // mapping of children
            const { tabChildren } = tab;
            delete tab.tabChildren;
            UICreatorUtils.prepareColumnsTableHeader(tab.data.columnsTableHeader);
            editTabs.push(tab);
            for (const tabChildId of tabChildren) {
              const tabChild = cloneDeep(apizWindow.tabs[tabChildId]); // cloneDeep is mandatory or it will be broken
              if (tabChild) {
                if (tabChild.data.isAdditionalInfo) {
                  additionalTabs.push(tabChild);
                } else {
                  UICreatorUtils.prepareColumnsTableHeader(tabChild.data.columnsTableHeader);
                  // setup the component to use if it's sortTab
                  tabChild.component = tabChild.data?.isSortTab ? 'SelectOrderComponent' : 'GridViewUiComponent';
                  tabChild.data.componentName = tabChild.data?.isSortTab
                    ? 'SelectOrderComponent'
                    : 'GridViewUiComponent';
                  tabChild.data.gridPaginator = true;
                  tabChild.data.isTabTopLevel = false;
                  delete tabChild.tabChildren;
                  // create the child
                  const childEditTab: ApizUIEditTab = {
                    ...cloneDeep(tabChild),
                    children: [tabChild],
                    component: 'EditTabUiComponent',
                  };
                  editTabs.push(childEditTab);
                }
              }
            }
            const iupicsTab: ApizTabUI = { editTabs, additionalTabs, key, tabId };
            this.iupicsTabs.set(tab.key, iupicsTab);

            //#183713
            // dans le cas du tab référencé : tab.windowId != windowId
            // #184057 check tablevel for windows with more than 1 tab
            if (tab.data.tabLevel === 0 && this.iupicsWindowDefaultTab.get(windowId)) {
              const originalKey = this.generateKey('WINDOW', windowId, this.iupicsWindowDefaultTab.get(windowId));
              if (originalKey !== tab.key) {
                this.iupicsTabs.set(originalKey, iupicsTab);
              }
            }
            //#183713
          }
          for (const ciDef of apizWindow.columnInfos ?? []) {
            this.#dataStore.setColumnInfo(ciDef.adTabId, ciDef.columnInfo);
          }
          for (const jsonDef of apizWindow.jsonDefs ?? []) {
            this.#dataStore.setJsonDef(jsonDef.jsonDefId, jsonDef);
          }
          for (const form of apizWindow.forms ?? []) {
            this.loadSpecificWindow(form.AD_Form_ID, of(form));
          }
          for (const process of apizWindow.processes ?? []) {
            this.#cacheService.loadProcessInCache(process.process.AD_Process_ID, this.#injector, of(process));
          }
          const key = this.generateKey('WINDOW', windowId, this.iupicsWindowDefaultTab.get(windowId));
          return this.iupicsTabs.get(key);
        })
      );
    }
  }

  getLocationPanel(ad_form_id: number, c_country_id = -1): Observable<IupicsSpecificWindow> {
    const key = c_country_id === -1 ? ad_form_id : parseInt(`999${ad_form_id}${c_country_id}`);
    if (!this.iupicsLocationPanels.has(key)) {
      const obs$ = this.#locationService.getLocationUI(ad_form_id, c_country_id).pipe(
        map((locationPanel) => {
          this.transformColumnInfosSpecificWindow(locationPanel);
          const errors = SpecificWindowsCreatorUtils.transformSpecificWindow(
            this.iupicsSpecificWindows,
            locationPanel,
            key
          );
          this.iupicsSpecificWindowsErrors.set(key, errors);
          return cloneDeep(this.iupicsSpecificWindows.get(key));
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        }),
        map(cloneDeep) // used to have a copy of the object each time we sub to avoid editing the object stored by the obs
      );
      this.iupicsLocationPanels.set(key, obs$);
    }
    const errors = this.iupicsSpecificWindowsErrors.get(key);
    if (errors && Object.keys(errors).length > 0) {
      this.#messageManager.newMessage(
        new IupicsMessage(
          this.#translateService.instant('specificWindow.itemsError'),
          errors.keys,
          'error',
          errors.values
        )
      );
    }
    return this.iupicsLocationPanels.get(key);
  }

  getSpecificWindow(ad_form_id: number | string): Observable<IupicsSpecificWindow> {
    let formId;
    if (typeof ad_form_id === 'number') {
      formId = ad_form_id;
    } else if (typeof ad_form_id === 'string') {
      if (this.iupicsSpecificWindowsValueID.has(ad_form_id)) {
        formId = this.iupicsSpecificWindowsValueID.get(ad_form_id);
      }
    }

    if (formId !== undefined && this.iupicsSpecificWindows.has(formId)) {
      const errors = this.iupicsSpecificWindowsErrors.get(formId);
      if (errors && Object.keys(errors).length > 0) {
        this.#messageManager.newMessage(
          new IupicsMessage(
            this.#translateService.instant('specificWindow.itemsError'),
            errors.keys,
            'error',
            errors.values
          )
        );
      }
      return of(cloneDeep(this.iupicsSpecificWindows.get(formId)));
    } else {
      return this.#windowService.getSpecificWindow(ad_form_id).pipe(
        map((specificWindow) => {
          if (typeof ad_form_id === 'string' && specificWindow.AD_Form_ID !== undefined) {
            this.iupicsSpecificWindowsValueID.set(ad_form_id, specificWindow.AD_Form_ID);
          }
          this.handleSpecificWindowFromWs(specificWindow.AD_Form_ID, specificWindow);
          return cloneDeep(
            specificWindow.AD_Form_ID !== undefined
              ? this.iupicsSpecificWindows.get(specificWindow.AD_Form_ID)
              : undefined
          );
        })
      );
    }
  }
  getWidgetEditorForm(): Observable<IupicsSpecificWindow> {
    return this.#widgetCenterService.getWidgetEditorForm().pipe(
      switchMap((specificWindow: SpecificWindowCompiereWS) => {
        this.handleSpecificWindowFromWs(specificWindow.AD_Form_ID, specificWindow);
        return this.getSpecificWindow(specificWindow.AD_Form_ID);
      })
    );
  }

  handleSpecificWindowFromWs(ad_form_id, specificWindow): void {
    this.iupicsWindowNames.set(ad_form_id, specificWindow.Name);
    this.transformColumnInfosSpecificWindow(specificWindow);
    const errors = SpecificWindowsCreatorUtils.transformSpecificWindow(this.iupicsSpecificWindows, specificWindow);
    //load in cache
    const specificWindowCached = this.iupicsSpecificWindows.get(ad_form_id);
    if (specificWindowCached) {
      const arr = [];
      for (const item of specificWindowCached.items) {
        if (item.component !== 'GridViewUiComponent') {
          if (item.processId) {
            this.#cacheService.loadProcessInCache(item.processId, this.#injector);
          } else if (item.formId) {
            arr.push(this.getSpecificWindow(item.formId));
          }
        }
      }
      if (arr.length > 0) {
        zip(...arr).subscribe();
      }
    }
    this.iupicsSpecificWindowsErrors.set(ad_form_id, errors);
  }

  loadSpecificWindow(ad_form_id: number, source?: Observable<ApizUIForm>): void {
    if (!source) {
      source = this.#windowService.getSpecificWindow(ad_form_id);
    }
    if (!this.iupicsWindowNames.has(ad_form_id)) {
      this.iupicsWindowNames.set(ad_form_id, 'loading');
      this.subscriptions.push(
        source.subscribe((specificWindow) => {
          this.iupicsWindowNames.set(ad_form_id, specificWindow.Name);
          this.transformColumnInfosSpecificWindow(specificWindow);
          const errors = SpecificWindowsCreatorUtils.transformSpecificWindow(
            this.iupicsSpecificWindows,
            specificWindow
          );
          this.iupicsSpecificWindowsErrors.set(ad_form_id, errors);
        })
      );
    }
  }

  getDBSelect(query: string, params: any[], tablenames: string[]): Observable<any[]> {
    return this.#calloutService.getData(query, params, tablenames);
  }

  executeProcess(params: ProcessRequest): Observable<any> {
    return this.#processSevice.executeProcess(params);
  }

  /**
   *
   * @param tabId number
   * @returns Observable<IupicsTab[]>: l'ensemble des tabs pour iupics
   */
  getTab(windowId: number, tabId: number): Observable<ApizTabUI> {
    const key = this.generateKey('WINDOW', windowId, tabId);
    if (this.iupicsTabs.has(key)) {
      return of(cloneDeep(this.iupicsTabs.get(key)));
    } else {
      // eslint-disable-next-line no-console
      console.error(`No tab found for ID: ${tabId}`);
      return of(undefined);
    }
  }
  /**
   * Returns all tabs for a given windowId.
   *
   * @param windowId number
   * @returns Observable<ApizTabUI[]> list of tabs belonging to that window
   */
  getAllTabs(windowId: number): Observable<ApizUIEditTab[]> {
    const prefix = `WINDOW_${windowId}`;

    const tabs: ApizUIEditTab[] = [];

    for (const [key, value] of this.iupicsTabs.entries()) {
      // Key format: WINDOW_windowId_tabId
      if (key.startsWith(prefix)) {
        if (value.editTabs) {
          value.editTabs.forEach((tab) => {
            tabs.push(cloneDeep(tab));
          });
        }
      }
    }

    // Optional: sort tabs by tabId (only works if tabId is numeric)
    tabs.sort((a, b) => (a.tabId ?? 0) - (b.tabId ?? 0));

    return of(tabs);
  }
  getCompiereTab(tab_id: number): Observable<CompiereTab> {
    if (!this.compiereTabs.has(tab_id)) {
      this.compiereTabs.set(
        tab_id,
        this.#windowService.getTab(tab_id).pipe(
          shareReplay({
            bufferSize: 1,
            refCount: true,
          }),
          map(cloneDeep)
        )
      );
    }
    return this.compiereTabs.get(tab_id);
  }

  zoomAcross(
    tableName: string,
    columnKey: string,
    record_id: number,
    isSOTrx?: boolean,
    window_id?: number
  ): Observable<any> {
    let tmpColumnKey = columnKey;
    if (tmpColumnKey && tmpColumnKey.split('.').length > 1) {
      tmpColumnKey = tmpColumnKey.split('.')[1];
    }
    return this.#windowService.getZoomAcross(tableName, tmpColumnKey, record_id, isSOTrx, window_id);
  }

  getZoomTarget(tab_id: number, columnKey: string, record_id: number): Observable<any> {
    return this.#windowService.getZoomTarget(tab_id, columnKey, record_id);
  }

  transformColumnInfos(compiereWindow: WindowCompiereWS): void {
    for (const tabEntity of compiereWindow.tabs) {
      const columnInfo: IupicsColumnInfo[] = [];
      for (const fieldEntity of tabEntity.fields) {
        columnInfo.push({
          fieldEntity: fieldEntity,
          filterType: UICreatorUtils.getFilterTypeFromReference(fieldEntity.field.AD_Reference_ID),
        });
      }
      this.#dataStore.setColumnInfo(tabEntity.tab.AD_Tab_ID, columnInfo);
    }
  }

  transformColumnInfosSpecificWindow(specificWindow: ApizUIForm): void {
    const items = specificWindow.detail.items.filter((item) => item.detail && item.detail.Type === 'T');
    for (const tableItem of items ?? []) {
      const columnInfo: ApizUIWindowColumnInfoDef[] = [];
      for (const column of tableItem.detail.items ?? []) {
        let fieldEntity = cloneDeep<ApizUIFieldEntity>(column);
        delete fieldEntity.field;
        fieldEntity = { ...fieldEntity, ...column.field };
        columnInfo.push({
          fieldEntity: fieldEntity,
          filterType: UICreatorUtils.getFilterTypeFromReference(column.field.field.AD_Reference_ID),
        });
      }
      this.#dataStore.setColumnInfo(tableItem.detail.AD_FormDetail_ID, columnInfo, true);
    }
  }

  getColumnInfos(id: number, fromSpecific = false): Observable<IupicsColumnInfo[]> {
    const prefix = fromSpecific ? 'AD_FormDetail_ID' : 'AD_Tab_ID';
    if (this.#dataStore.hasColumnInfo(id, fromSpecific)) {
      return of(this.#dataStore.getColumnInfo(id, fromSpecific) as IupicsColumnInfo[]);
    } else {
      return throwError(() => new Error(`No column infos for ${prefix} : ${id}`));
    }
  }

  getColumnInfosSync(id: number, fromSpecific = false): IupicsColumnInfo[] {
    const prefix = fromSpecific ? 'AD_FormDetail_ID' : 'AD_Tab_ID';
    if (this.#dataStore.hasColumnInfo(id, fromSpecific)) {
      return this.#dataStore.getColumnInfo(id, fromSpecific) as IupicsColumnInfo[];
    } else {
      throw new Error(`No column infos for ${prefix} : ${id}`);
    }
  }
  getFieldsData(url: string): Observable<any> {
    url = url.replace(/\/\//g, '/');
    return this.#compiereDataService.getFieldsData(url);
  }

  getCupsPrinters(): Observable<any> {
    return this.#printerService.getCupsPrinters();
  }

  /*getLocationPanel(ad_form_id: number, c_country_id: number): Observable<IupicsSpecificWindow> {
    c_country_id = c_country_id ? c_country_id : -1;
    return this.locationService.getLocationByCountry(ad_form_id, c_country_id).pipe(
      map((specificWindow) => {
        if (specificWindow) {
          this.iupicsWindowNames.set(ad_form_id, specificWindow.Name);
          this.transformColumnInfosSpecificWindow(specificWindow);
          const errors = SpecificWindowsCreatorUtils.transformSpecificWindow(this.iupicsSpecificWindows, specificWindow);

          this.iupicsSpecificWindowsErrors.set(ad_form_id, errors);
          return this.iupicsSpecificWindows.get(ad_form_id);
        }
      })
    );
  }*/

  ngOnDestroy(): void {
    SubscriptionsUtils.unsubscribe(...this.subscriptions);
  }
}
